import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, Code } from 'lucide-react';
import { useState, useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import { WidgetSettingsDto } from '@/Domain/api/WIdget/dto/WidgetSetting.dto';
import { usePreview } from '@/Domain/components/PreviewProvider';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { resolveWidgetHeaderForeground } from '@/UI/Website/Appearance/contrast';

const DOCS_URL = 'https://docs.quickchat.ai/channels/website/#advanced-features-custom-parameters';

const DocsLink = ({ children }: { children?: React.ReactNode }) => (
  <a
    href={DOCS_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="text-foreground font-medium hover:underline inline-flex items-center gap-0.5"
  >
    {children}
    <ArrowUpRight className="h-3.5 w-3.5" />
  </a>
);

export const AdvancedSettings = () => {
  const { t } = useTranslation('ai_preview_page');
  const { metadata, setMetadata } = usePreview();
  const { data: widgetSettings } = useQuery<WidgetSettingsDto>({
    queryKey: [QueryKey.widgetSettings],
  });
  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [jsonError, setJsonError] = useState(false);
  const [localMetadata, setLocalMetadata] = useState(metadata);

  useEffect(() => {
    setLocalMetadata(metadata);
  }, [metadata]);

  const formatJson = useCallback((value: string): string => {
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return value;
    }
  }, []);

  const validateJson = useCallback((value: string): boolean => {
    if (!value.trim()) return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }, []);

  const handleMetadataChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setLocalMetadata(value);
      const isValid = validateJson(value);
      setJsonError(!isValid);
      if (isValid) {
        setMetadata(value);
      }
    },
    [validateJson, setMetadata],
  );

  const handleMetadataBlur = useCallback(() => {
    if (!jsonError && localMetadata.trim()) {
      const formatted = formatJson(localMetadata);
      setLocalMetadata(formatted);
      setMetadata(formatted);
    }
  }, [localMetadata, jsonError, formatJson, setMetadata]);

  const triggerLabel = t('advanced_settings.trigger_label');

  // Trigger overlays the widget header (a cross-origin iframe), so match its text color.
  const headerForeground = widgetSettings
    ? resolveWidgetHeaderForeground(widgetSettings.theme, widgetSettings.widget_theme_custom)
    : undefined;

  return (
    <>
      <TooltipProvider delayDuration={200}>
        <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
          <TooltipTrigger asChild>
            <IconButton
              type="button"
              variant="secondary"
              size="sm"
              className="absolute top-2 right-16 z-20 mt-[2px] !h-[34px] !w-[34px] !rounded-full !border-0 !bg-transparent hover:!bg-widget-button-hover"
              style={headerForeground ? { color: headerForeground } : undefined}
              onClick={() => {
                setTooltipOpen(false);
                setOpen(true);
              }}
              aria-label={triggerLabel}
              data-testid="advanced-settings-trigger"
            >
              <Code />
            </IconButton>
          </TooltipTrigger>
          <TooltipContent>{t('advanced_settings.tooltip')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="advanced-settings-dialog">
          <DialogHeader>
            <DialogTitle>
              <Code className="w-5 h-5" />
              {t('advanced_settings.title')}
            </DialogTitle>
            <DialogDescription>
              <Trans
                i18nKey="ai_preview_page:advanced_settings.conversation_metadata.description"
                components={{ docs_link: <DocsLink /> }}
              />
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <div className="relative">
              <AutosizeTextarea
                value={localMetadata}
                onChange={handleMetadataChange}
                onBlur={handleMetadataBlur}
                placeholder={t('advanced_settings.conversation_metadata.placeholder')}
                error={jsonError}
                className="font-mono text-xs min-h-[300px] px-4 bg-card border-border"
              />
              {jsonError && (
                <p className="text-destructive text-xs mt-1.5">
                  {t('advanced_settings.conversation_metadata.invalid_json')}
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
